import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Receipt as ReceiptIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { invoicesService, Invoice, CreateInvoiceData, InvoiceItem, invoiceStatusOptions } from '../services/invoicesService';
import { clientsService, Client } from '../services/clientsService';

const InvoicesPage: React.FC = () => {
  const theme = useTheme();
  
  // State
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [sending, setSending] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<{
    clientId: string;
    items: InvoiceItem[];
    dueDate: string;
    notes: string;
  }>({
    clientId: '',
    items: [{ description: '', quantity: 1, unitPrice: 0 }],
    dueDate: '',
    notes: '',
  });
  
  // Notification state
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await invoicesService.getInvoices({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        search: searchText || undefined,
        status: statusFilter || undefined,
      });
      setInvoices(response.data);
      setTotalInvoices(response.pagination.total);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      setSnackbar({ open: true, message: 'Failed to load invoices', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [paginationModel, searchText, statusFilter]);

  // Fetch clients for the create dialog
  const fetchClients = async () => {
    try {
      const response = await clientsService.getClients({ limit: 100 });
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchClients();
  }, [fetchInvoices]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (paginationModel.page !== 0) {
        setPaginationModel({ ...paginationModel, page: 0 });
      } else {
        fetchInvoices();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Handle form changes
  const handleFormChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement | { value: unknown }>) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  // Handle item changes
  const handleItemChange = (index: number, field: keyof InvoiceItem) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const newItems = [...formData.items];
    const value = field === 'description' ? event.target.value : Number(event.target.value);
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  // Add item
  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unitPrice: 0 }],
    });
  };

  // Remove item
  const handleRemoveItem = (index: number) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData({ ...formData, items: newItems });
    }
  };

  // Calculate total
  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
  };

  // Open dialog for new invoice
  const handleAddInvoice = () => {
    const defaultDueDate = new Date();
    defaultDueDate.setDate(defaultDueDate.getDate() + 30);
    
    setFormData({
      clientId: '',
      items: [{ description: '', quantity: 1, unitPrice: 0 }],
      dueDate: defaultDueDate.toISOString().split('T')[0],
      notes: '',
    });
    setDialogOpen(true);
  };

  // Create invoice
  const handleCreateInvoice = async () => {
    try {
      await invoicesService.createInvoice({
        clientId: formData.clientId,
        items: formData.items,
        dueDate: formData.dueDate,
        notes: formData.notes || undefined,
      });
      setSnackbar({ open: true, message: 'Invoice created successfully', severity: 'success' });
      setDialogOpen(false);
      fetchInvoices();
    } catch (error) {
      console.error('Failed to create invoice:', error);
      setSnackbar({ open: true, message: 'Failed to create invoice', severity: 'error' });
    }
  };

  // View invoice details
  const handleViewInvoice = async (invoice: Invoice) => {
    try {
      const fullInvoice = await invoicesService.getInvoice(invoice.id);
      setSelectedInvoice(fullInvoice);
      setViewDialogOpen(true);
    } catch (error) {
      console.error('Failed to load invoice details:', error);
      setSnackbar({ open: true, message: 'Failed to load invoice details', severity: 'error' });
    }
  };

  // Send invoice
  const handleSendInvoice = async (invoice: Invoice) => {
    setSending(invoice.id);
    try {
      await invoicesService.sendInvoice(invoice.id);
      setSnackbar({ open: true, message: 'Invoice sent successfully', severity: 'success' });
      fetchInvoices();
    } catch (error) {
      console.error('Failed to send invoice:', error);
      setSnackbar({ open: true, message: 'Failed to send invoice', severity: 'error' });
    } finally {
      setSending(null);
    }
  };

  // Download PDF
  const handleDownloadPDF = async (invoice: Invoice) => {
    try {
      if (!invoice.pdf_url) {
        // Generate PDF first
        const result = await invoicesService.generatePDF(invoice.id);
        window.open(result.pdfUrl, '_blank');
      } else {
        window.open(invoice.pdf_url, '_blank');
      }
    } catch (error) {
      console.error('Failed to download PDF:', error);
      setSnackbar({ open: true, message: 'Failed to download PDF', severity: 'error' });
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Format status
  const formatStatus = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Get status color
  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'draft': theme.palette.grey[500],
      'sent': theme.palette.info.main,
      'paid': theme.palette.success.main,
      'overdue': theme.palette.error.main,
      'cancelled': theme.palette.warning.main,
    };
    return colors[status?.toLowerCase()] || theme.palette.grey[500];
  };

  // Columns definition
  const columns: GridColDef[] = [
    {
      field: 'invoice_number',
      headerName: 'Invoice ID',
      width: 140,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
          <Typography variant="body2" fontWeight={500}>{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: 'client_name',
      headerName: 'Client',
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight={500}>
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Date Issued',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleDateString() : '-'}
        </Typography>
      ),
    },
    {
      field: 'due_date',
      headerName: 'Due Date',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleDateString() : '-'}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const color = getStatusColor(params.value);
        return (
          <Chip
            label={formatStatus(params.value)}
            size="small"
            sx={{
              bgcolor: alpha(color, 0.15),
              color: color,
              border: `1px solid ${color}`,
              fontWeight: 500,
            }}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title="View">
            <IconButton
              size="small"
              sx={{ color: 'text.secondary' }}
              onClick={() => handleViewInvoice(params.row)}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download PDF">
            <IconButton
              size="small"
              sx={{ color: 'text.secondary' }}
              onClick={() => handleDownloadPDF(params.row)}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Send">
            <IconButton
              size="small"
              sx={{ color: 'primary.main' }}
              onClick={() => handleSendInvoice(params.row)}
              disabled={sending === params.row.id || params.row.status === 'paid'}
            >
              {sending === params.row.id ? (
                <CircularProgress size={18} />
              ) : (
                <SendIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            Invoices
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {totalInvoices} total invoices
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchInvoices}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddInvoice}
            sx={{
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            Create Invoice
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Search invoices..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                label="Status Filter"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {invoiceStatusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    {formatStatus(status)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => {
                setSearchText('');
                setStatusFilter('');
              }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Grid */}
      <Paper
        sx={{
          height: 600,
          width: '100%',
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
          '& .MuiDataGrid-root': {
            border: 'none',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: 'text.primary',
          },
          '& .MuiDataGrid-columnHeaders': {
            bgcolor: 'background.default',
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: 'text.primary',
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.default',
            color: 'text.primary',
          },
          '& .MuiTablePagination-root': {
            color: 'text.primary',
          },
        }}
      >
        <DataGrid
          rows={invoices}
          columns={columns}
          loading={loading}
          rowCount={totalInvoices}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          checkboxSelection
          disableRowSelectionOnClick
          onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
          rowSelectionModel={selectedRows}
          slots={{
            loadingOverlay: () => (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            ),
          }}
        />
      </Paper>

      {/* Create Invoice Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Invoice</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Client"
                value={formData.clientId}
                onChange={handleFormChange('clientId')}
                required
              >
                <MenuItem value="">Select a client</MenuItem>
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.name} {client.company ? `(${client.company})` : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                value={formData.dueDate}
                onChange={handleFormChange('dueDate')}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Line Items
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell width={100}>Qty</TableCell>
                    <TableCell width={120}>Unit Price</TableCell>
                    <TableCell width={100}>Total</TableCell>
                    <TableCell width={50}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          value={item.description}
                          onChange={handleItemChange(index, 'description')}
                          placeholder="Description"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={item.quantity}
                          onChange={handleItemChange(index, 'quantity')}
                          inputProps={{ min: 1 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={item.unitPrice}
                          onChange={handleItemChange(index, 'unitPrice')}
                          inputProps={{ min: 0, step: 0.01 }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatCurrency(item.quantity * item.unitPrice)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveItem(index)}
                          disabled={formData.items.length <= 1}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button size="small" startIcon={<AddIcon />} onClick={handleAddItem} sx={{ mt: 1 }}>
                Add Line Item
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Typography variant="h6">
                  Total: {formatCurrency(calculateTotal())}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={handleFormChange('notes')}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateInvoice}
            disabled={!formData.clientId || !formData.dueDate || formData.items.every(i => !i.description)}
          >
            Create Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Invoice Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Invoice {selectedInvoice?.invoice_number}
        </DialogTitle>
        <DialogContent>
          {selectedInvoice && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Client</Typography>
                <Typography>{selectedInvoice.client_name}</Typography>
                {selectedInvoice.client_company && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedInvoice.client_company}
                  </Typography>
                )}
                {selectedInvoice.client_email && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedInvoice.client_email}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip
                  label={formatStatus(selectedInvoice.status)}
                  size="small"
                  sx={{
                    bgcolor: alpha(getStatusColor(selectedInvoice.status), 0.15),
                    color: getStatusColor(selectedInvoice.status),
                    border: `1px solid ${getStatusColor(selectedInvoice.status)}`,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Date Issued</Typography>
                <Typography>{new Date(selectedInvoice.created_at).toLocaleDateString()}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Due Date</Typography>
                <Typography>{new Date(selectedInvoice.due_date).toLocaleDateString()}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Line Items
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedInvoice.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                        <TableCell align="right">{formatCurrency(item.quantity * item.unitPrice)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3} align="right"><strong>Total</strong></TableCell>
                      <TableCell align="right"><strong>{formatCurrency(selectedInvoice.amount)}</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Grid>
              {selectedInvoice.notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                  <Typography>{selectedInvoice.notes}</Typography>
                </Grid>
              )}
              {selectedInvoice.payment_link && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Payment Link</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    href={selectedInvoice.payment_link}
                    target="_blank"
                  >
                    View Payment Page
                  </Button>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          {selectedInvoice && (
            <>
              <Button
                startIcon={<DownloadIcon />}
                onClick={() => handleDownloadPDF(selectedInvoice)}
              >
                Download PDF
              </Button>
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={() => {
                  handleSendInvoice(selectedInvoice);
                  setViewDialogOpen(false);
                }}
                disabled={selectedInvoice.status === 'paid'}
              >
                Send Invoice
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InvoicesPage;